// ============================================================
// RecipientAccessPanel — the owner's complete recipient lifecycle for ONE
// client portal (End User Portal Activation, Phase 7).
//
// Issue secure access (live, with the one-time code revealed exactly once and
// the invitation link copyable), view every recipient's real status, suspend/
// restore, revoke (live, two-layer, honest about the external authority), and
// reissue. Every mutation re-reads SERVER TRUTH — there is no optimistic
// success anywhere, and revoke/issue outcomes render exactly what the server
// reported (including the degraded revoked_local_only state).
//
// Requires a portal linked to a canonical project — unlinked portals show an
// honest explanation instead of a dead control.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { api, type AccessGrantView } from '../../../../api/client'
import { CC_INK, CC_MONO } from '../../shell'
import { SectionCard, StateBlock, chipBtn, inputStyle } from './PortalKit'
import { humanizePortalError } from './portalFactory'

export interface RecipientAccessPanelProps {
  portalId: string
  /** Whether the portal is linked to a canonical project (issuance precondition). */
  linked: boolean
  portalName: string
}

const STATUS_COLOR: Record<AccessGrantView['status'], string> = {
  issued: '#0891b2',
  redeemed: '#059669',
  suspended: '#b45309',
  revoked: '#dc2626',
  expired: '#6b7280',
}

function inviteUrl(g: { invite_path: string }): string {
  return typeof window !== 'undefined' ? `${window.location.origin}${g.invite_path}` : g.invite_path
}

interface IssueResult {
  code: string
  invitePath: string
  grantRecorded: boolean
  message: string
}

export default function RecipientAccessPanel({ portalId, linked, portalName }: RecipientAccessPanelProps) {
  const [grants, setGrants] = useState<AccessGrantView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  // Issue form state — a reissue prefills it from the source grant.
  const [showIssue, setShowIssue] = useState(false)
  const [label, setLabel] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [ndaRequired, setNdaRequired] = useState(true)
  const [issued, setIssued] = useState<IssueResult | null>(null)

  const reload = useCallback(async () => {
    try {
      const r = await api.access.grants({ portal_id: portalId })
      setGrants(r.grants || [])
      setError(null)
    } catch (e) {
      setError(humanizePortalError(e))
    }
  }, [portalId])

  useEffect(() => { void reload() }, [reload])

  const issue = useCallback(async () => {
    if (!label.trim()) { setNotice('A label is required to issue access.'); return }
    setBusy(true)
    setNotice(null)
    try {
      const r = await api.access.create({
        portal_id: portalId,
        label: label.trim(),
        recipient: {
          ...(recipientName.trim() ? { name: recipientName.trim() } : {}),
          ...(recipientEmail.trim() ? { email: recipientEmail.trim() } : {}),
        },
        nda_required: ndaRequired,
        live: true,
      })
      // Server truth only: r.access_code exists exactly once; the grant list
      // re-reads from the API. grant_recorded:false is surfaced honestly.
      setIssued({
        code: r.access_code,
        invitePath: r.invite_path ?? `/portal/${r.invite.id}`,
        grantRecorded: r.grant_recorded === true,
        message: r.message,
      })
      setShowIssue(false)
      setLabel(''); setRecipientName(''); setRecipientEmail(''); setNdaRequired(true)
      await reload()
    } catch (e) {
      setNotice(humanizePortalError(e))
    }
    setBusy(false)
  }, [portalId, label, recipientName, recipientEmail, ndaRequired, reload])

  const act = useCallback(async (fn: () => Promise<{ message?: string } | unknown>) => {
    setBusy(true)
    setNotice(null)
    try {
      const r = (await fn()) as { message?: string }
      if (r && typeof r.message === 'string') setNotice(r.message)
      await reload()
    } catch (e) {
      setNotice(humanizePortalError(e))
      await reload()
    }
    setBusy(false)
  }, [reload])

  const copy = useCallback((text: string, what: string) => {
    void navigator.clipboard?.writeText(text)
    setNotice(`${what} copied.`)
  }, [])

  return (
    <SectionCard
      title="Recipient Access"
      accent="#0891b2"
      right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{grants?.length ?? 0} issued</span>}
    >
      {!linked && (
        <p style={{ margin: 0, fontSize: 11, color: CC_INK.muted, lineHeight: 1.55 }}>
          Link this portal to a canonical project first — secure access is always
          issued for the linked project, so an unlinked portal cannot issue.
        </p>
      )}

      {linked && (
        <>
          {notice && (
            <p role="status" style={{ margin: '0 0 10px', fontSize: 11, color: '#b8862a', lineHeight: 1.5 }}>{notice}</p>
          )}

          {/* One-time code reveal — shown exactly once after a live issue. */}
          {issued && (
            <div style={{ border: '1px solid rgba(8,145,178,0.4)', borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: CC_INK.secondary, marginBottom: 6 }}>{issued.message}</div>
              <div style={{ fontFamily: CC_MONO, fontSize: 14, color: CC_INK.primary, marginBottom: 8 }}>{issued.code}</div>
              {!issued.grantRecorded && (
                <div style={{ fontSize: 10.5, color: '#dc2626', marginBottom: 8 }}>
                  Project binding was NOT recorded — the link may not open the right workspace.
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <button type="button" style={chipBtn} onClick={() => copy(issued.code, 'One-time code')}>Copy code</button>
                <button
                  type="button"
                  style={chipBtn}
                  onClick={() => copy(typeof window !== 'undefined' ? `${window.location.origin}${issued.invitePath}` : issued.invitePath, 'Invitation link')}
                >
                  Copy invitation link
                </button>
                <button type="button" style={chipBtn} onClick={() => setIssued(null)}>Done — hide code</button>
              </div>
            </div>
          )}

          {!showIssue && (
            <div style={{ marginBottom: 12 }}>
              <button type="button" style={chipBtn} disabled={busy} onClick={() => setShowIssue(true)}>+ Issue secure access</button>
            </div>
          )}

          {showIssue && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              <input style={inputStyle} value={label} placeholder={`Access label (e.g. ${portalName} — client review)`} onChange={(e) => setLabel(e.target.value)} aria-label="Access label" />
              <input style={inputStyle} value={recipientName} placeholder="Recipient name (optional)" onChange={(e) => setRecipientName(e.target.value)} aria-label="Recipient name" />
              <input style={inputStyle} value={recipientEmail} placeholder="Recipient email (optional — for your records)" onChange={(e) => setRecipientEmail(e.target.value)} aria-label="Recipient email" />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: CC_INK.secondary }}>
                <input type="checkbox" checked={ndaRequired} onChange={(e) => setNdaRequired(e.target.checked)} />
                Require confidentiality agreement before entry
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" style={chipBtn} disabled={busy} onClick={() => void issue()}>Issue live access</button>
                <button type="button" style={chipBtn} disabled={busy} onClick={() => { setShowIssue(false); setNotice(null) }}>Cancel</button>
              </div>
              <p style={{ margin: 0, fontSize: 10, color: CC_INK.faint, lineHeight: 1.5 }}>
                Issuing creates a real invitation with a one-time code, bound to the
                linked project. Nothing is emailed automatically — copy the link and
                code and deliver them yourself.
              </p>
            </div>
          )}

          {error && <StateBlock kind="error" title="Couldn’t load recipients" body={error} />}
          {!error && grants === null && <StateBlock kind="loading" title="Loading recipients…" />}
          {!error && grants !== null && grants.length === 0 && (
            <StateBlock kind="empty" title="No recipients yet" body="Issue secure access to invite the first recipient." />
          )}

          {!error && grants !== null && grants.map((g) => (
            <div key={g.id} style={{ padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 9, fontFamily: CC_MONO, fontWeight: 800, color: STATUS_COLOR[g.status], minWidth: 74 }}>
                  {g.status.toUpperCase()}
                </span>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 12.5, color: CC_INK.primary }}>{g.label}</div>
                  <div style={{ fontSize: 10, color: CC_INK.muted, fontFamily: CC_MONO }}>
                    {g.recipient?.name || g.recipient?.email || 'unnamed recipient'}
                    {' · issued '}{g.issued_at.slice(0, 10)}
                    {g.redeemed_at ? ` · redeemed ${g.redeemed_at.slice(0, 10)}` : ''}
                    {g.revoked_at ? ` · revoked ${g.revoked_at.slice(0, 10)}${g.engine_confirmed_revoked ? '' : ' (authority unconfirmed)'}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {g.status !== 'revoked' && (
                    <button type="button" style={{ ...chipBtn, fontSize: 10, padding: '4px 8px' }} onClick={() => copy(inviteUrl(g), 'Invitation link')}>Copy link</button>
                  )}
                  {(g.status === 'issued' || g.status === 'redeemed') && (
                    <button type="button" style={{ ...chipBtn, fontSize: 10, padding: '4px 8px' }} disabled={busy}
                      onClick={() => void act(() => api.access.grantAction({ action: 'suspend', grant_id: g.id }))}>
                      Suspend
                    </button>
                  )}
                  {g.status === 'suspended' && (
                    <button type="button" style={{ ...chipBtn, fontSize: 10, padding: '4px 8px' }} disabled={busy}
                      onClick={() => void act(() => api.access.grantAction({ action: 'restore', grant_id: g.id }))}>
                      Restore
                    </button>
                  )}
                  {g.status !== 'revoked' && (
                    <button type="button" style={{ ...chipBtn, fontSize: 10, padding: '4px 8px', color: '#dc2626' }} disabled={busy}
                      onClick={() => void act(() => api.access.revoke({ grant_id: g.id }))}>
                      Revoke
                    </button>
                  )}
                  <button type="button" style={{ ...chipBtn, fontSize: 10, padding: '4px 8px' }} disabled={busy}
                    onClick={() => {
                      setShowIssue(true)
                      setLabel(g.label)
                      setRecipientName((g.recipient?.name as string) || '')
                      setRecipientEmail((g.recipient?.email as string) || '')
                      setNdaRequired(g.nda_required)
                      setNotice('Reissue: review the prefilled details, then issue. The old invitation stays as-is until you revoke it.')
                    }}>
                    Reissue
                  </button>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </SectionCard>
  )
}